import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, computed, PropType } from 'vue';
import { Bar } from 'vue-chartjs';
import { ChartConfig } from './chartConfig';
import ColourService from '@/shared/services/color';
import { Data, Series, SeriesConfig, SeriesOrderConfiguration } from '../../models';
import merge from 'lodash.merge';


export default /*@__PURE__*/_defineComponent({
  __name: 'PercentageBarChart',
  props: {
  data: {
    type: Object as PropType<Data>,
    required: true,
  },
  reportId: {
    type: String,
    required: true,
  },
  seriesOrder: {
    type: Array as PropType<SeriesOrderConfiguration>,
    required: true,
  },
  seriesToCompareTo: {
    type: String,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const renderSeriesInReverse = ref(true);

const seriesConfig = computed(() => {
  if (typeof props.seriesOrder[0] === 'string') {
    return (props.seriesOrder as string[]).map((name) => ({ name, displayName: name }));
  }
  return props.seriesOrder as SeriesConfig[];
});

const seriesRenderOrder = computed(() =>
  renderSeriesInReverse.value ? seriesConfig.value.slice().reverse() : seriesConfig.value,
);

const orderedSeries = computed(() => seriesRenderOrder.value.map((seriesConfig) => getSeries(seriesConfig.name)));

const getSeries = (name: string): Series => props.data.series.find((series) => series.name === name)!;

const getSeriesConfig = (name: string): SeriesConfig =>
  seriesConfig.value.find((config) => config.name === name) || { name, displayName: name };

const getPercentageValues = (seriesData: Series): number[] =>
  seriesData.data.map((dataPoint, index) => {
    const totalValue = getSeries(props.seriesToCompareTo).data[index];
    return totalValue === 0 ? 0 : Math.round((dataPoint / totalValue) * 10000) / 100;
  });

const chartData = computed(() => {
  const backgroundPalette = ChartConfig.borderPalette(seriesRenderOrder.value);
  const borderPalette = ChartConfig.borderPalette(seriesRenderOrder.value);

  return {
    labels: props.data.scale.labels,
    datasets: orderedSeries.value.map((series, index) => ({
      label: getSeriesConfig(series.name).displayName,
      data: getPercentageValues(series),
      backgroundColor: backgroundPalette[index],
      borderColor: borderPalette[index],
      hoverBackgroundColor: ColourService.setAlpha(backgroundPalette[index], 0.4),
      borderWidth: 1,
    })),
  };
});

const chartOptions = computed(() => {
  const overrides = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        reverse: renderSeriesInReverse.value,
        position: 'right',
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: any) => `${tooltipItem.raw}%`,
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: props.data.scale.title },
        ticks: { maxTicksLimit: 32 },
        grid: { display: false },
      },
      y: {
        min: 0, // Replaced beginAtZero with min
        max: 100,
        ticks: {
          callback: (value: number) => (value % 1 === 0 ? `${value}%` : undefined),
        },
      },
    },
  };

  return merge({}, ChartConfig.gridChartsGlobalOptions, overrides);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Bar), {
    options: chartOptions.value,
    data: chartData.value
  }, null, 8, ["options", "data"]))
}
}

})